/**
 * FreeBatchApplyPage — public "Apply Now" flow for a 100% free batch.
 *
 * This page never asks for payment. The admin's `free_batch_config`
 * decides which fields render, whether a promo code is required, and
 * the referral-code input is always available (Phase 3 will credit
 * the referrer). A prominent one-shot warning appears just before the
 * final submit so students can't edit later.
 *
 * On success the student gets a nice confirmation with their OWN
 * referral code (so they can share instantly, even before the email
 * lands) plus a "sign in" CTA that jumps to /login.
 */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import { CheckCircle2, AlertTriangle, Loader2, Share2, Copy, Check, ArrowRight } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Field labels + input types — kept in sync with backend `_ALLOWED_FIELDS`.
const FIELD_META = {
  // Personal
  name:              { label: "Full name",         type: "text",  placeholder: "As on your CNIC / B-Form" },
  email:             { label: "Email",             type: "email", placeholder: "you@example.com" },
  phone:             { label: "Phone",             type: "tel",   placeholder: "+92 300 1234567" },
  whatsapp:          { label: "WhatsApp number",   type: "tel",   placeholder: "+92 300 …" },
  cnic:              { label: "CNIC / B-Form",     type: "text",  placeholder: "35202-1234567-8" },
  date_of_birth:     { label: "Date of birth",     type: "date",  placeholder: "" },
  gender:            { label: "Gender",            type: "text",  placeholder: "Male / Female" },
  // Location
  city:              { label: "City",              type: "text",  placeholder: "Lahore" },
  province:          { label: "Province",          type: "text",  placeholder: "Punjab" },
  country:           { label: "Country",           type: "text",  placeholder: "Pakistan" },
  address:           { label: "Address",           type: "text",  placeholder: "Street + area" },
  postal_code:       { label: "Postal code",       type: "text",  placeholder: "54000" },
  // Academic
  school:            { label: "School",            type: "text",  placeholder: "Your school" },
  college:           { label: "College",           type: "text",  placeholder: "Your college" },
  class:             { label: "Class",             type: "text",  placeholder: "e.g. F.Sc Part 2" },
  roll_number:       { label: "Roll number",       type: "text",  placeholder: "" },
  matric_marks:      { label: "Matric marks",      type: "text",  placeholder: "e.g. 1050/1100" },
  fsc_marks:         { label: "F.Sc marks",        type: "text",  placeholder: "e.g. 985/1100" },
  board:             { label: "Board",             type: "text",  placeholder: "Lahore / Federal / …" },
  student_type:      { label: "Student type",      type: "text",  placeholder: "Fresher / Repeater" },
  // Guardian
  father_name:       { label: "Father's name",     type: "text",  placeholder: "" },
  father_phone:      { label: "Father's phone",    type: "tel",   placeholder: "+92 300 …" },
  father_occupation: { label: "Father's occupation", type: "text", placeholder: "" },
  mother_name:       { label: "Mother's name",     type: "text",  placeholder: "" },
  mother_phone:      { label: "Mother's phone",    type: "tel",   placeholder: "+92 300 …" },
  guardian_email:    { label: "Guardian email",    type: "email", placeholder: "" },
  // Misc
  referrer_name:     { label: "Referred by",       type: "text",  placeholder: "Name of the person who referred you" },
  how_did_you_hear:  { label: "How did you hear about us?", type: "text", placeholder: "Instagram, friend, teacher, …" },
  goals:             { label: "Your goals",        type: "text",  placeholder: "e.g. MDCAT 2027, ICS improver, …" },
};

export default function FreeBatchApplyPage() {
  const { slug } = useParams();
  const [schema, setSchema] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [values, setValues] = useState({});
  const [promo, setPromo] = useState("");
  const [referrer, setReferrer] = useState("");
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API_URL}/api/free-batch/${slug}/form-schema`)
      .then((r) => { if (!cancelled) setSchema(r.data); })
      .catch((e) => {
        if (cancelled) return;
        const d = e?.response?.data?.detail;
        setErr(typeof d === "string" ? d : (d?.message || "Unable to load this free batch."));
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [slug]);

  const canSubmit = useMemo(() => {
    if (!schema || submitting || result) return false;
    if (schema.promo_code_required && !promo.trim()) return false;
    if (!consent) return false;
    for (const f of schema.fields) {
      if (!(values[f] || "").trim()) return false;
    }
    return true;
  }, [schema, values, promo, consent, submitting, result]);

  const submit = async () => {
    setSubmitting(true); setErr("");
    try {
      const { data } = await axios.post(`${API_URL}/api/free-batch/${slug}/register`, {
        fields: values,
        promo_code: promo.trim(),
        referrer_code: referrer.trim() || null,
        consent_acknowledged: consent,
      });
      setResult(data);
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = typeof d === "string" ? d : (d?.message || "Submission failed. Please try again.");
      setErr(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="fb-loading">
        <Loader2 className="animate-spin text-slate-400" size={26} />
      </div>
    );
  }

  if (err && !schema) {
    return <ErrorScreen message={err} />;
  }

  if (result) {
    return <SuccessScreen result={result} batchName={schema?.batch?.name} />;
  }

  return (
    <div className="min-h-screen" style={{ background: "var(--px-paper, #eef0f7)" }} data-testid="fb-apply-page">
      <div className="max-w-2xl mx-auto px-4 py-10 sm:py-16">
        <Header batch={schema?.batch} />

        <div className="bg-white rounded-[20px] p-6 sm:p-8" style={{ border: "1px solid rgba(15,23,42,0.08)", boxShadow: "0 12px 30px -18px rgba(15,23,42,0.15)" }}>
          {/* Fields */}
          <div className="space-y-4">
            {schema.fields.map((f) => {
              const meta = FIELD_META[f] || { label: f, type: "text" };
              return (
                <div key={f}>
                  <label className="block text-[11.5px] font-semibold uppercase tracking-[0.12em] text-slate-500 mb-1.5">
                    {meta.label}
                  </label>
                  <input
                    type={meta.type}
                    value={values[f] || ""}
                    onChange={(e) => setValues({ ...values, [f]: e.target.value })}
                    placeholder={meta.placeholder}
                    className="w-full h-11 px-3.5 rounded-[10px] text-[14px] outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-400 transition"
                    style={{ background: "#fff", border: "1px solid rgba(15,23,42,0.12)", color: "#0f172a" }}
                    data-testid={`fb-field-${f}`}
                    autoComplete="off"
                  />
                </div>
              );
            })}

            {schema.promo_code_required && (
              <div>
                <label className="block text-[11.5px] font-semibold uppercase tracking-[0.12em] text-slate-500 mb-1.5">
                  Promo code <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  value={promo}
                  onChange={(e) => setPromo(e.target.value.toUpperCase())}
                  placeholder="Enter your promo code"
                  className="w-full h-11 px-3.5 rounded-[10px] text-[14px] font-mono outline-none focus:ring-2 focus:ring-blue-500/40"
                  style={{ background: "#fff", border: "1px solid rgba(15,23,42,0.12)", color: "#0f172a" }}
                  data-testid="fb-field-promo"
                />
              </div>
            )}

            <div>
              <label className="block text-[11.5px] font-semibold uppercase tracking-[0.12em] text-slate-500 mb-1.5">
                Referral code <span className="text-slate-400 normal-case font-normal tracking-normal text-[11px] ml-1">(optional — helps a friend unlock content)</span>
              </label>
              <input
                type="text"
                value={referrer}
                onChange={(e) => setReferrer(e.target.value.toUpperCase())}
                placeholder="e.g. ABC123XY"
                className="w-full h-11 px-3.5 rounded-[10px] text-[14px] font-mono outline-none focus:ring-2 focus:ring-blue-500/40"
                style={{ background: "#fff", border: "1px solid rgba(15,23,42,0.12)", color: "#0f172a" }}
                data-testid="fb-field-referrer"
              />
            </div>
          </div>

          {/* One-shot warning */}
          <div
            className="mt-6 p-4 rounded-[12px] flex gap-3"
            style={{ background: "#fef3c7", border: "1px solid #f59e0b" }}
            data-testid="fb-one-shot-warning"
          >
            <AlertTriangle size={16} className="text-amber-700 shrink-0 mt-0.5" />
            <div className="text-[12.5px] leading-[1.55] text-amber-900">
              {schema.one_shot_warning}
            </div>
          </div>

          {/* Consent */}
          <label className="mt-4 flex items-start gap-2.5 cursor-pointer" data-testid="fb-consent-label">
            <input
              type="checkbox"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              className="mt-0.5 w-4 h-4 accent-blue-600 cursor-pointer"
              data-testid="fb-consent"
            />
            <span className="text-[12.5px] text-slate-700 leading-[1.5]">
              I understand this is a <strong>one-shot submission</strong> and I&apos;ve reviewed my information. My details cannot be changed after submitting.
            </span>
          </label>

          {err && (
            <div className="mt-4 p-3 rounded-[10px] text-[13px] flex items-start gap-2"
                 style={{ background: "#fef2f2", border: "1px solid #fecaca", color: "#b91c1c" }}
                 data-testid="fb-error">
              <AlertTriangle size={14} className="mt-0.5 shrink-0" /><div>{err}</div>
            </div>
          )}

          <button
            onClick={submit}
            disabled={!canSubmit}
            className="mt-6 w-full h-12 rounded-[12px] font-semibold text-[14px] flex items-center justify-center gap-2 transition disabled:opacity-40 disabled:cursor-not-allowed"
            style={{
              background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
              color: "#fff",
              boxShadow: "0 6px 18px -6px rgba(15,23,42,0.35)",
            }}
            data-testid="fb-submit"
          >
            {submitting ? <><Loader2 size={16} className="animate-spin" /> Submitting…</> : <>Submit registration <ArrowRight size={16} /></>}
          </button>
        </div>
      </div>
    </div>
  );
}

function Header({ batch }) {
  return (
    <div className="mb-8">
      <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold tracking-[0.14em] uppercase mb-4"
           style={{ background: "#dcfce7", color: "#166534", border: "1px solid #86efac" }}>
        <CheckCircle2 size={11} strokeWidth={2.5} /> 100% Free
      </div>
      <h1 className="text-[28px] sm:text-[36px] font-bold leading-[1.1] tracking-[-0.02em] text-slate-900" data-testid="fb-batch-name">
        {batch?.name || "Apply for the free batch"}
      </h1>
      <p className="mt-2 text-[14px] text-slate-500 leading-[1.55]">
        Fill in your details below. We&apos;ll email your sign-in credentials + your own referral code the moment you submit.
      </p>
    </div>
  );
}

function SuccessScreen({ result, batchName }) {
  const [copied, setCopied] = useState(false);
  const [pwShown, setPwShown] = useState(false);
  const shareText = `Hey! I just registered for LogicsWallah *${batchName || "free batch"}* — it's 100% free. Use my referral code *${result.referral_code}* when you register. https://logicswallah.pk/free/${window.location.pathname.split("/").pop()}`;

  const copyCode = () => {
    try { navigator.clipboard.writeText(result.referral_code); setCopied(true); setTimeout(() => setCopied(false), 1400); } catch { /* silent */ }
  };
  const copyPassword = () => {
    try { navigator.clipboard.writeText(result.temp_password || ""); setPwShown(true); setTimeout(() => setPwShown(false), 1400); } catch { /* silent */ }
  };
  const shareWhatsApp = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(shareText)}`, "_blank");
  };

  return (
    <div className="min-h-screen" style={{ background: "var(--px-paper, #eef0f7)" }} data-testid="fb-success">
      <div className="max-w-xl mx-auto px-4 py-12 sm:py-16">
        <div className="bg-white rounded-[20px] p-6 sm:p-8 text-center" style={{ border: "1px solid rgba(15,23,42,0.08)", boxShadow: "0 12px 30px -18px rgba(15,23,42,0.15)" }}>
          <div className="w-14 h-14 rounded-full mx-auto flex items-center justify-center mb-4" style={{ background: "#dcfce7", border: "1px solid #86efac" }}>
            <CheckCircle2 size={26} className="text-emerald-600" strokeWidth={2.4} />
          </div>
          <h1 className="text-[24px] font-bold text-slate-900 mb-1">You&apos;re in!</h1>
          <p className="text-[13.5px] text-slate-500 leading-[1.6]">
            Your account is ready — credentials are shown below and also emailed to you. First sign-in on a new device may ask for a one-time code from your inbox.
          </p>

          {/* Instant credentials card */}
          {result.temp_password && (
            <div
              className="mt-6 mx-auto max-w-sm p-5 rounded-[14px] text-left"
              style={{ background: "#fefce8", border: "1px dashed #ca8a04" }}
              data-testid="fb-credentials-card"
            >
              <div className="text-[10.5px] font-semibold tracking-[0.16em] uppercase text-amber-700 mb-2">
                Your sign-in credentials
              </div>
              <div className="text-[10.5px] font-semibold text-slate-500 uppercase tracking-[0.10em]">Email</div>
              <div className="text-[14px] font-mono text-slate-900 break-all mb-2" data-testid="fb-credential-email">
                {result.login_email}
              </div>
              <div className="text-[10.5px] font-semibold text-slate-500 uppercase tracking-[0.10em]">Temporary password</div>
              <div className="flex items-center gap-2">
                <div className="flex-1 text-[15px] font-mono tracking-[0.10em] text-slate-900 bg-white rounded-[8px] px-2.5 py-1.5 border border-amber-300 select-all" data-testid="fb-credential-password">
                  {result.temp_password}
                </div>
                <button
                  onClick={copyPassword}
                  className="h-9 px-2.5 rounded-[8px] text-[12px] font-semibold flex items-center gap-1"
                  style={{ background: "#fde047", color: "#111827" }}
                  data-testid="fb-copy-password"
                  aria-label="Copy password"
                >
                  {pwShown ? <Check size={12} /> : <Copy size={12} />} {pwShown ? "Copied" : "Copy"}
                </button>
              </div>
              <div className="text-[11px] text-amber-700 mt-2 leading-[1.4]">
                You&apos;ll be asked to change this on first sign-in.
              </div>
            </div>
          )}

          <div
            className="mt-4 mx-auto max-w-sm p-5 rounded-[14px]"
            style={{ background: "#f0f9ff", border: "1px dashed #3b82f6" }}
            data-testid="fb-referral-card"
          >
            <div className="text-[10.5px] font-semibold tracking-[0.16em] uppercase text-blue-700 mb-2">
              Your referral code
            </div>
            <div className="text-[26px] font-bold font-mono tracking-[0.24em] text-slate-900" data-testid="fb-referral-code">
              {result.referral_code}
            </div>
            <div className="mt-4 flex gap-2">
              <button
                onClick={copyCode}
                className="flex-1 h-10 rounded-[10px] text-[13px] font-medium flex items-center justify-center gap-1.5 transition"
                style={{ background: "#fff", border: "1px solid rgba(15,23,42,0.12)", color: "#0f172a" }}
                data-testid="fb-copy-code"
              >
                {copied ? <Check size={13} /> : <Copy size={13} />} {copied ? "Copied" : "Copy code"}
              </button>
              <button
                onClick={shareWhatsApp}
                className="flex-1 h-10 rounded-[10px] text-[13px] font-semibold flex items-center justify-center gap-1.5 transition"
                style={{ background: "#25d366", color: "#fff" }}
                data-testid="fb-share-whatsapp"
              >
                <Share2 size={13} /> WhatsApp
              </button>
            </div>
          </div>

          <a href="/login"
             className="mt-6 inline-flex items-center gap-1.5 text-[13px] font-semibold text-blue-700 hover:text-blue-900"
             data-testid="fb-login-link">
            Sign in to the portal <ArrowRight size={14} />
          </a>
        </div>
      </div>
    </div>
  );
}

function ErrorScreen({ message }) {
  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: "var(--px-paper, #eef0f7)" }} data-testid="fb-error-screen">
      <div className="max-w-md w-full bg-white rounded-[20px] p-6 text-center" style={{ border: "1px solid #fecaca" }}>
        <div className="w-12 h-12 mx-auto rounded-full flex items-center justify-center mb-3" style={{ background: "#fef2f2", border: "1px solid #fecaca" }}>
          <AlertTriangle size={20} className="text-rose-600" />
        </div>
        <h1 className="text-[20px] font-bold text-slate-900 mb-1">Unable to load</h1>
        <p className="text-[13px] text-slate-500 leading-[1.6]">{message}</p>
      </div>
    </div>
  );
}
